import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import {
  useI18N,
  useCreatePublicVenueNote,
  VenueDetailsBySlugResponse,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import { BusinessNoteHeadingSuggestion } from "common/types";

import Loader from "@/components/common/Loader";
import { useCustomToast } from "@/hooks/useCustomToast";
import { XIcon, ChevronDown, Plus } from "lucide-react";
import { InfoIcon } from "@phosphor-icons/react";
import { Textarea } from "@/shadcn/components/ui/textarea";
import WideButton from "@/components/common/WideButton";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import { useDarkMode } from "@/hooks/useDarkMode";
import { createPageAnalytics } from "common/analytics";
import SelectDropDown from "@/components/common/SelectDropDown";
import RadioButton from "@/components/common/RadioButton";
import { StandardButton } from "@/components/common/StandardButton";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/shadcn/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcn/components/ui/popover";
import { DateRange } from "react-day-picker";
import { businessNotesAll } from "common/types";
// Edit venue note component - handles only editing existing notes
import { cn } from "@/lib/utils";

/** Headline placeholders from CMS: [XIcon], free-text [x]/[X], and [Space] (resolved from area). */
function parseVenueNoteHeadlineTemplate(template: string) {
  const parts = template.split(/(\[XIcon\]|\[[xX]\]|\[Space\])/);
  return parts.map((part, index) => ({
    text: part,
    isPlaceholder: /^\[(XIcon|[xX]|Space)\]$/.test(part),
    isLocationPlaceholder: /^\[Space\]$/.test(part),
    id: index,
  }));
}

/** True if the headline string includes any templated segment that needs substitution. */
function headlineRequiresTemplateParsing(heading: string) {
  return /\[(?:XIcon|[xX]|Space)\]/.test(heading);
}

function EditVenueNote(props: {
  venueDetailsResponse: VenueDetailsBySlugResponse;
  onClose: () => void;
  onInfoClick: () => void;
  note: businessNotesAll; // Required for edit mode
}) {
  const i18n = useI18N();
  const { modalStyle, modalClassName } = useModalHeight();
  const showToast = useCustomToast();

  const [notes, setNotes] = useState(props.note.business_note_text || "");
  const {
    data: mutateResult,
    error,
    status,
    mutate,
  } = useCreatePublicVenueNote();
  const noteRef = useRef(null);
  const isDark = useDarkMode();
  const [applyToWholeVenue, setApplyToWholeVenue] = useState(
    props.note.location_type === "Business",
  );

  // Find the initial area details for the existing note
  const initialAreaDetails = useMemo(() => {
    if (props.note.location_id) {
      console.log(
        "?? Initializing area details for location_id:",
        props.note.location_id,
      );

      // First try to find the section that contains the location_id
      const section =
        props.venueDetailsResponse.details.businessVenueNotesAreaLocations.find(
          (e) => e.id.toString() === props.note.location_id.toString(),
        );

      if (section) {
        console.log(
          "? Found matching section:",
          section.name,
          "with ID:",
          section.id,
        );
        console.log(
          "?? Section tags:",
          section.tags.map((tag) => ({ id: tag.id, name: tag.name })),
        );

        // CheckIcon if location_id is actually a parent ID (matches section ID)
        if (section.id.toString() === props.note.location_id.toString()) {
          console.log(
            "?? Location_id is a parent ID - selecting parent without subId",
          );
          return {
            parentId: Number(section.id),
            subId: "", // Empty subId for parent selection
          };
        }

        // If location_id is not the section ID, it might be a tag ID
        const matchingTag = section.tags.find(
          (tag) => tag.id.toString() === props.note.location_id.toString(),
        );

        if (matchingTag) {
          console.log(
            "?? Found matching tag:",
            matchingTag.name,
            "with ID:",
            matchingTag.id,
          );
          return {
            parentId: Number(section.id),
            subId: matchingTag.id.toString(),
          };
        }

        // Fallback: if no matching tag found, use first tag (old behavior)
        console.log("?? No matching tag found, using first tag as fallback");
        return {
          parentId: Number(section.id),
          subId: section.tags[0]?.id.toString() || "",
        };
      }

      // If we didn't find the section, try to find a tag that matches the location_id
      console.log("?? Section not found, searching through all tags...");
      for (const section of props.venueDetailsResponse.details
        .businessVenueNotesAreaLocations) {
        const matchingTag = section.tags.find(
          (tag) => tag.id.toString() === props.note.location_id.toString(),
        );
        if (matchingTag) {
          console.log(
            "?? Found matching tag in section:",
            section.name,
            "tag:",
            matchingTag.name,
          );
          return {
            parentId: Number(section.id),
            subId: matchingTag.id.toString(),
          };
        }
      }

      console.log(
        "? No matching section or tag found for location_id:",
        props.note.location_id,
      );
    }
    return null;
  }, [
    props.note.location_id,
    props.venueDetailsResponse.details.businessVenueNotesAreaLocations,
  ]);

  const [selectedArea, setSelectedArea] = useState<{
    parentId: number;
    subId: string;
  } | null>(initialAreaDetails);

  const [selectedPriority, setSelectedPriority] = useState(
    props.note.business_notes_action_id,
  );
  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    props.note.schedule_date_from && props.note.schedule_date_to
      ? {
          from: new Date(props.note.schedule_date_from),
          to: new Date(props.note.schedule_date_to),
        }
      : undefined,
  );
  const [title, setTitle] = useState(props.note.business_note_title);
  const [previewTitle, setPreviewTitle] = useState(
    props.note.business_note_title,
  );
  const [styledPreviewTitle, setStyledPreviewTitle] = useState(
    props.note.business_note_title,
  );

  // Add state for tracking template suggestion and placeholder values
  const [selectedTemplateSuggestionId, setSelectedTemplateSuggestionId] =
    useState<string | null>(
      props.note.business_note_heading_suggestions_id?.toString() || null,
    );
  const [placeholderValues, setPlaceholderValues] = useState<string[]>(
    props.note.business_note_heading_suggestions_placeholder_text
      ? props.note.business_note_heading_suggestions_placeholder_text.split("|")
      : [],
  );

  const [isManualAdd, setIsManualAdd] = useState(false);
  const [isManualHeadline, setIsManualHeadline] = useState(false);

  // State to track when suggestions are visible
  const [suggestionsVisible, setSuggestionsVisible] = useState(false);

  // State to track filtered suggestions based on area selection
  const [filteredSuggestions, setFilteredSuggestions] = useState<
    BusinessNoteHeadingSuggestion[]
  >([]);

  // Ref to track when we're switching templates to prevent useEffect interference
  const isSwitchingTemplate = useRef(false);

  // SearchableInput state variables
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(
    props.note.business_note_heading_suggestions_id
      ? props.venueDetailsResponse.details.businessNoteHeadingSuggestions.find(
          (suggestion) =>
            suggestion.id.toString() ===
            props.note.business_note_heading_suggestions_id?.toString(),
        )?.heading || null
      : null,
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<string>("");

  const activeHeadlineTemplate = useMemo(() => {
    if (selectedTemplate) return selectedTemplate;
    if (!title) return null;
    const parts = parseVenueNoteHeadlineTemplate(title);
    return parts.some(
      (part) => part.isPlaceholder && !part.isLocationPlaceholder,
    )
      ? title
      : null;
  }, [selectedTemplate, title]);

  useEffect(() => {
    if (!activeHeadlineTemplate) {
      if (!props.note?.business_note_heading_suggestions_id) {
        setPlaceholderValues([]);
      }
      return;
    }
    const count = parseVenueNoteHeadlineTemplate(activeHeadlineTemplate).filter(
      (part) => part.isPlaceholder && !part.isLocationPlaceholder,
    ).length;
    setPlaceholderValues((prev) => {
      if (prev.length === count) return prev;
      if (prev.length > count) return prev.slice(0, count);
      return [...prev, ...new Array(count - prev.length).fill("")];
    });
  }, [activeHeadlineTemplate, selectedArea, applyToWholeVenue, props.note]);

  // Determine if suggestions dropdown should be enabled
  const isSuggestionsEnabled = useMemo(() => {
    // Enabled if applying to whole venue OR if specific area is selected
    return applyToWholeVenue || selectedArea !== null;
  }, [applyToWholeVenue, selectedArea]);

  // Close dropdown when suggestions become disabled
  useEffect(() => {
    if (!isSuggestionsEnabled && isDropdownOpen) {
      setIsDropdownOpen(false);
      setSuggestionsVisible(false);
      setSearchTerm("");
      setSelectedIndex(-1);
    }
  }, [isSuggestionsEnabled, isDropdownOpen]);

  // Clear suggestion state when switching between whole venue yes/no
  useEffect(() => {
    // Only clear suggestion state if we're not editing an existing note with template
    // This prevents clearing template state when editing existing notes
    if (props.note && props.note.business_note_heading_suggestions_id) {
      return;
    }

    // Clear all suggestion-related state when applyToWholeVenue changes
    setSelectedTemplateSuggestionId(null);
    setSelectedTemplate(null);
    setPlaceholderValues([]);
    setTitle("");
    setPreviewTitle("");
    setStyledPreviewTitle("");
    setIsManualAdd(false);
    setSelectedPriority(0);

    // Close dropdown if open
    setIsDropdownOpen(false);
    setSuggestionsVisible(false);
    setSearchTerm("");
    setSelectedIndex(-1);
  }, [applyToWholeVenue]);

  // Zoom awareness state and logic
  const isZoomedIn = useZoomDetection();

  // if title is empty, set isManualHeadline to false and selectedPriority to 0
  useEffect(() => {
    // Only clear template state if we're not editing an existing note with template
    if (title.length === 0) {
      setIsManualHeadline(false);
      setSelectedPriority(0);

      // Only clear template state if we're not editing an existing note with template
      if (!(props.note && props.note.business_note_heading_suggestions_id)) {
        setSelectedTemplateSuggestionId(null);
        setPlaceholderValues([]);
        setIsManualAdd(false); // Reset manual mode when title is cleared
      }
    }
  }, [title]);

  // Set priority based on whether title matches any suggested heading
  useEffect(() => {
    const matchingSuggestionContainsTitle = filteredSuggestions.find(
      (suggestion) =>
        suggestion.heading.toLowerCase().includes(title.toLowerCase()) ||
        title.toLowerCase().includes(suggestion.heading.toLowerCase()),
    );

    if (matchingSuggestionContainsTitle) {
      setIsManualHeadline(false);
      setSelectedPriority(
        matchingSuggestionContainsTitle.business_notes_action_id,
      );
      // Note: Date range selection is now always available, no need to set hasEndDate
    } else {
      // CheckIcon if the current title matches any of the suggested headings
      const matchingSuggestion = filteredSuggestions.find(
        (suggestion) => suggestion.heading === title,
      );

      // Set isManualHeadline to true if title is not empty and doesn't match any suggestion (manual headline)
      if (title.length > 0 && !matchingSuggestion && selectedPriority === 0) {
        setIsManualHeadline(true);
        setSelectedPriority(10);
        const slotCount = parseVenueNoteHeadlineTemplate(title).filter(
          (p) => p.isPlaceholder && !p.isLocationPlaceholder,
        ).length;
        if (slotCount === 0) {
          setPlaceholderValues([]);
        } else {
          setPlaceholderValues((prev) =>
            prev.length === slotCount
              ? prev
              : new Array(slotCount).fill(""),
          );
        }
        // Note: Date range selection is now always available
      }
    }
  }, [title, filteredSuggestions, isManualHeadline, selectedPriority]);

  const handleSuggestionSelect = useCallback((business_notes_action_id) => {
    console.log(
      "handleSuggestionSelect business_notes_action_id",
      business_notes_action_id,
    );
    setSelectedPriority(business_notes_action_id);

    // If business_notes_action_id is 0, it means manual mode
    if (business_notes_action_id === 0) {
      setIsManualAdd(true);
      // Clear template suggestion ID when switching to manual mode
      console.log("Clearing selectedTemplateSuggestionId for manual mode");
      setSelectedTemplateSuggestionId(null);
    } else {
      // For all other suggestion types, set manual mode to false
      setIsManualAdd(false);
    }

    // Note: Date range selection is now always available, no need to handle hasEndDate logic
  }, []);

  const selectedAreaDetails = useMemo(() => {
    if (selectedArea) {
      const section =
        props.venueDetailsResponse.details.businessVenueNotesAreaLocations.find(
          (e) => e.id.toString() === selectedArea.parentId.toString(),
        );
      const subSection = section?.tags.find(
        (e) => e.id.toString() === selectedArea.subId,
      );
      return {
        parentId: selectedArea.parentId,
        parentName: section?.name,
        subId: selectedArea.subId,
        subName: subSection?.name,
        location_type: section?.location_type,
      };
    }
    return undefined;
  }, [
    selectedArea,
    props.venueDetailsResponse.details.businessVenueNotesAreaLocations,
  ]);

  const selectedPriorityDetails = useMemo(() => {
    return props.venueDetailsResponse.details.businessNotesActionsVenueNotes?.find(
      (action) => action.id === selectedPriority,
    );
  }, [
    selectedPriority,
    props.venueDetailsResponse.details.businessNotesActionsVenueNotes,
  ]);

  const isNoteTextMandatory = useMemo(() => {
    return selectedPriorityDetails?.isNoteTextMandatory === 1;
  }, [selectedPriorityDetails]);

  useEffect(() => {
    if (error) {
      showToast(error.message, "error");
    }
  }, [error]);

  // edit_alert_form page (open/close/alert_edited). alert_id is numeric in the plan.
  const editFormA = createPageAnalytics("edit_alert_form");
  const editFormBaseProps = () => ({
    alert_id: Number(props.note?.business_note_text_id ?? 0),
    brand_id: props.venueDetailsResponse?.details?.brandId != null ? String(props.venueDetailsResponse?.details?.brandId) : "",
    brand_name: props.venueDetailsResponse?.details?.brandName ?? "",
    business_id:
      props.venueDetailsResponse?.details?.business?.id != null
        ? String(props.venueDetailsResponse.details.business.id)
        : "",
    place_name: props.venueDetailsResponse?.details?.name ?? "",
  });

  // edit_alert_form_opened — mount, ref-guarded against StrictMode's double mount-effect.
  const editOpenedFiredRef = useRef(false);
  useEffect(() => {
    if (editOpenedFiredRef.current) return;
    editOpenedFiredRef.current = true;
    editFormA.track("page_view_page", editFormBaseProps());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // edit_alert_form_closed — once, on explicit close (X). The post-submit close goes through the
  // success effect's onClose() below, which does NOT call this (alert_edited fires instead).
  const editClosedFiredRef = useRef(false);
  const fireEditFormClosed = () => {
    if (editClosedFiredRef.current) return;
    editClosedFiredRef.current = true;
    editFormA.track("close_action_button", editFormBaseProps());
  };

  useEffect(() => {
    if (mutateResult) {
      console.log("mutateResult", mutateResult);
      showToast(i18n.get("noteUpdatedSuccessfully"), "success");
      props.onClose();
    }
  }, [mutateResult]);

  const submit = () => {
    //Fallback to non-time-sensitive priority if no priority is selected
    if (selectedPriority === 0) {
      setSelectedPriority(10);
    }

    const selectedPriorityDetails =
      props.venueDetailsResponse.details.businessNotesActionsVenueNotes.find(
        (e) => e.id.toString() === selectedPriority.toString(),
      );

    let locationId = props.venueDetailsResponse.details.business.id.toString();
    let locationType = "Business";
    if (!applyToWholeVenue && selectedAreaDetails) {
      locationId =
        selectedAreaDetails?.subId && selectedAreaDetails?.subId !== "0"
          ? selectedAreaDetails?.subId.toString()
          : selectedAreaDetails?.parentId.toString();
      locationType = selectedAreaDetails.location_type || "";
    }

    // Prepare template data
    let business_note_heading_suggestions_id: string | null = null;
    let placeholder_text: string | null = null;
    let finalTitle = title;

    // Log the template suggestion ID for debugging
    console.log(
      "Submitting with selectedTemplateSuggestionId state:",
      selectedTemplateSuggestionId,
    );
    console.log("isManualAdd state:", isManualAdd);

    if (isManualAdd || !selectedTemplateSuggestionId) {
      // Manual mode or no template: send null for both fields
      business_note_heading_suggestions_id = null;
      placeholder_text = null;
      console.log(
        "Submitting manual headline with null values for template fields",
      );
      console.log(
        "Manual mode detected - clearing template suggestion ID and placeholder text",
      );

      // Safety check: ensure template suggestion ID is cleared when in manual mode
      if (isManualAdd && selectedTemplateSuggestionId) {
        console.log(
          "Safety check: clearing selectedTemplateSuggestionId in manual mode",
        );
        setSelectedTemplateSuggestionId(null);
      }
    } else {
      // Template mode: submit the template suggestion ID and placeholder text
      business_note_heading_suggestions_id = selectedTemplateSuggestionId;
      finalTitle = previewTitle || title;
      placeholder_text = placeholderValues.join("|") || "";
      console.log("Submitting template with final preview text:", finalTitle);
      console.log("Placeholder values:", placeholder_text);
      console.log(
        "Template mode detected - using suggestion ID:",
        business_note_heading_suggestions_id,
      );
    }

    // Validate template data consistency
    if (business_note_heading_suggestions_id && !placeholder_text) {
      console.warn("Template suggestion ID provided but no placeholder text");
    }

    // Log final values being sent to API
    console.log("Final API payload values:");
    console.log(
      "- business_note_heading_suggestions_id:",
      business_note_heading_suggestions_id,
    );
    console.log("- placeholder_text:", placeholder_text);
    console.log("- finalTitle:", finalTitle);
    console.log("- isManualAdd:", isManualAdd);

    // save_changes_action_button -> alert_edited (on submit; fires next to the legacy update event).
    editFormA.track("save_changes_action_button", {
      alert_location_name: applyToWholeVenue
        ? "Whole venue"
        : selectedAreaDetails?.parentName ?? "",
      alert_title: finalTitle,
      // business_notes_action_id (held in selectedPriority): 9 = Live Alert, else note.
      alert_type: selectedPriority === 9 ? "live_alerts" : "notes",
      business_id:
        props.venueDetailsResponse?.details?.business?.id != null
          ? String(props.venueDetailsResponse.details.business.id)
          : "",
      photos_count:
        props.venueDetailsResponse?.details?.businessPhotos?.length ?? 0,
      place_name: props.venueDetailsResponse?.details?.name ?? "",
    });

    mutate({
      noteId: props.note.business_note_text_id?.toString() || "",
      businessId:
        props.venueDetailsResponse.details.business.id.toString() || "",
      note: notes,
      title: finalTitle,
      notePriority: selectedPriorityDetails?.id.toString() || "",
      noteAreaType: locationType || "",
      noteAreaId: locationId || "",
      dateFrom: dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : "",
      dateTo: dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : "",
      business_note_heading_suggestions_id,
      placeholder_text,
    });
  };

  const isSubmitDisabled = () => {
    // CheckIcon if priority is selected and title has minimum length
    const basicValidation = selectedPriority === 0 || title.length < 5;

    // CheckIcon if note text is mandatory and empty
    const noteTextValidation = isNoteTextMandatory && notes.length < 5;

    // CheckIcon if venue location is properly selected when not applying to whole venue
    const locationValidation = !applyToWholeVenue && selectedArea === null;

    const bracketSlots = activeHeadlineTemplate
      ? parseVenueNoteHeadlineTemplate(activeHeadlineTemplate).filter(
          (part) => part.isPlaceholder && !part.isLocationPlaceholder,
        )
      : [];

    const placeholdersFilled =
      bracketSlots.length === 0 ||
      bracketSlots.every((_, idx) =>
        (placeholderValues[idx] ?? "").trim().length > 0,
      );

    // Note: Date range (note validity) is optional - not included in validation
    // UsersIcon can choose to add it or not without affecting submit button state

    // For other priorities, check basic validation, note text validation, and location validation
    const result =
      basicValidation ||
      noteTextValidation ||
      locationValidation ||
      !placeholdersFilled;

    if (noteTextValidation) {
      console.log("Submit disabled due to mandatory note text:", {
        isNoteTextMandatory,
        notesLength: notes.length,
        notes,
      });
    }

    if (locationValidation) {
      console.log("Submit disabled due to location validation:", {
        applyToWholeVenue,
        selectedArea,
        locationValidation,
      });
    }

    console.log("Submit validation:", {
      selectedPriority,
      titleLength: title.length,
      basicValidation,
      isNoteTextMandatory,
      notesLength: notes.length,
      noteTextValidation,
      locationValidation,
      applyToWholeVenue,
      selectedArea,
      dateRangeFrom: dateRange?.from, // For debugging - not used in validation
      result,
    });

    return result;
  };

  // Update previewTitle when title changes (for non-template cases)
  useEffect(() => {
    // For template notes, don't update preview here - it will be handled by template-specific logic
    if (
      props.note &&
      props.note.business_note_heading_suggestions_id &&
      activeHeadlineTemplate
    ) {
      return;
    }

    // For non-template notes, update preview with the title
    setPreviewTitle(title);
  }, [title, props.note, activeHeadlineTemplate]);

  // Initialize template data when editing an existing note
  useEffect(() => {
    if (props.note) {
      // If editing an existing note, initialize template-related state
      if (props.note.business_note_heading_suggestions_id) {
        setSelectedTemplateSuggestionId(
          props.note.business_note_heading_suggestions_id.toString(),
        );

        // Find the corresponding template suggestion
        const templateSuggestion =
          props.venueDetailsResponse.details.businessNoteHeadingSuggestions.find(
            (suggestion) =>
              suggestion.id.toString() ===
              props.note.business_note_heading_suggestions_id?.toString(),
          );

        if (templateSuggestion) {
          // For template notes, show the template with placeholders in the title field
          // This allows users to see and edit the template structure
          setSelectedTemplate(templateSuggestion.heading);
          setTitle(templateSuggestion.heading);

          // Initialize placeholder values array if template has placeholders
          if (headlineRequiresTemplateParsing(templateSuggestion.heading)) {
            const parts = parseVenueNoteHeadlineTemplate(templateSuggestion.heading);
            const placeholdersCount = parts.filter(
              (part) => part.isPlaceholder && !part.isLocationPlaceholder,
            ).length;

            // If we have placeholder text from the note, use it; otherwise initialize empty array
            if (props.note.business_note_heading_suggestions_placeholder_text) {
              const placeholderValuesArray =
                props.note.business_note_heading_suggestions_placeholder_text.split(
                  "|",
                );
              setPlaceholderValues(placeholderValuesArray);
            } else {
              const initialPlaceholderValues = new Array(
                placeholdersCount,
              ).fill("");
              setPlaceholderValues(initialPlaceholderValues);
            }
          }
        } else {
          console.warn(
            "Template suggestion not found for ID:",
            props.note.business_note_heading_suggestions_id,
          );
        }
      } else {
        // If no template suggestion ID, this might be a manual note
        setSelectedTemplate(null);
        setPlaceholderValues([]);
      }
    }
  }, [
    props.note,
    props.venueDetailsResponse.details.businessNoteHeadingSuggestions,
  ]);

  // Update preview immediately after template initialization
  useEffect(() => {
    if (props.note && activeHeadlineTemplate && placeholderValues.length > 0) {
      // Update preview with all placeholders replaced
      let previewText = activeHeadlineTemplate;

      // Replace [Space] placeholders with selected location
      previewText = previewText.replace(
        /\[Space\]/g,
        selectedLocation || "[Space]",
      );

      const xPlaceholders =
        parseVenueNoteHeadlineTemplate(activeHeadlineTemplate).filter(
          (part) => part.isPlaceholder && !part.isLocationPlaceholder,
        );

      xPlaceholders.forEach((placeholder, placeholderIndex) => {
        const placeholderValue =
          placeholderValues[placeholderIndex] || placeholder.text;
        previewText = previewText.replace(placeholder.text, placeholderValue);
      });

      setPreviewTitle(previewText);

      const styledPreview = generateStyledPreview(
        activeHeadlineTemplate,
        placeholderValues,
        selectedLocation || "",
      );
      setStyledPreviewTitle(styledPreview);
    } else if (
      props.note &&
      activeHeadlineTemplate &&
      placeholderValues.length === 0
    ) {
      // If we have a template but no placeholder values, initialize empty ones
      const parts = parseVenueNoteHeadlineTemplate(activeHeadlineTemplate);
      const placeholdersCount = parts.filter(
        (part) => part.isPlaceholder && !part.isLocationPlaceholder,
      ).length;
      if (placeholdersCount > 0) {
        const initialPlaceholderValues = new Array(placeholdersCount).fill("");
        setPlaceholderValues(initialPlaceholderValues);
      }
    }
  }, [activeHeadlineTemplate, placeholderValues, selectedLocation, props.note]);

  // Debug placeholder values changes
  useEffect(() => {
    console.log("Placeholder values changed:", placeholderValues);
  }, [placeholderValues]);

  // Debug template suggestion ID changes
  useEffect(() => {
    console.log(
      "selectedTemplateSuggestionId changed to:",
      selectedTemplateSuggestionId,
    );
  }, [selectedTemplateSuggestionId]);

  // Debug selectedTemplate changes
  useEffect(() => {
    console.log("selectedTemplate changed to:", selectedTemplate);
  }, [selectedTemplate]);

  // Update preview when editing existing note with template
  useEffect(() => {
    // Skip if we're in the process of switching templates
    if (isSwitchingTemplate.current) {
      console.log("Skipping template preview update - switching templates");
      return;
    }

    // console.log("Template preview useEffect triggered:", {
    // hasNote: !!props.note,
    // selectedTemplate,
    // placeholderValuesLength: placeholderValues.length,
    // title
    // });

    if (props.note && activeHeadlineTemplate && placeholderValues.length > 0) {
      console.log("Updating preview for editing existing template note");

      // Update preview with all placeholders replaced
      let previewText = activeHeadlineTemplate;

      // Replace [Space] placeholders with selected location
      previewText = previewText.replace(
        /\[Space\]/g,
        selectedLocation || "[Space]",
      );

      const xPlaceholders = parseVenueNoteHeadlineTemplate(
        activeHeadlineTemplate,
      ).filter(
        (part) => part.isPlaceholder && !part.isLocationPlaceholder,
      );

      xPlaceholders.forEach((placeholder, placeholderIndex) => {
        const placeholderValue =
          placeholderValues[placeholderIndex] || placeholder.text;
        previewText = previewText.replace(placeholder.text, placeholderValue);
      });

      setPreviewTitle(previewText);

      const styledPreview = generateStyledPreview(
        activeHeadlineTemplate,
        placeholderValues,
        selectedLocation || "",
      );
      setStyledPreviewTitle(styledPreview);
    } else if (props.note && !activeHeadlineTemplate && title) {
      // If no template is selected but we have a title, update preview to show the title directly
      console.log("Updating preview for non-template note");
      setPreviewTitle(title);
      setStyledPreviewTitle(title);
    }
  }, [
    props.note,
    activeHeadlineTemplate,
    placeholderValues,
    selectedLocation,
    title,
  ]);

  // Additional useEffect to handle when selectedTemplate changes
  useEffect(() => {
    // console.log("Template change useEffect triggered:", {
    // hasNote: !!props.note,
    // selectedTemplate,
    // title,
    // isSwitchingTemplate: isSwitchingTemplate.current
    // });

    // Skip if we're in the process of switching templates
    if (isSwitchingTemplate.current) {
      console.log("Skipping template change update - switching templates");
      return;
    }

    if (props.note && !activeHeadlineTemplate && title) {
      // When template is cleared, ensure preview shows the title
      console.log("Template cleared, updating preview to show title");
      setPreviewTitle(title);
      setStyledPreviewTitle(title);

      // Also ensure placeholder inputs are hidden by clearing related state
      if (placeholderValues.length > 0) {
        console.log("Clearing remaining placeholder values");
        setPlaceholderValues([]);
      }
      // Don't clear selectedTemplateSuggestionId here - it should persist for the selected suggestion
    }
  }, [selectedTemplate, title, props.note, placeholderValues.length, activeHeadlineTemplate]);

  // Force update useEffect to handle template state inconsistencies
  useEffect(() => {
    // Only run this when we're not actively switching templates
    if (isSwitchingTemplate.current) {
      return;
    }

    if (
      props.note &&
      title &&
      !selectedTemplate &&
      selectedTemplateSuggestionId
    ) {
      console.log(
        "Detected template state inconsistency, clearing template suggestion ID",
      );
      setSelectedTemplateSuggestionId(null);
    }

    // Additional check: if we have a non-template title but still have template state
    if (
      props.note &&
      title &&
      !selectedTemplate &&
      !headlineRequiresTemplateParsing(title)
    ) {
      console.log(
        "Detected non-template title with template state, clearing all template state",
      );
      if (placeholderValues.length > 0) {
        setPlaceholderValues([]);
      }
      if (selectedTemplateSuggestionId) {
        setSelectedTemplateSuggestionId(null);
      }
    }
  }, [
    props.note,
    title,
    selectedTemplate,
    selectedTemplateSuggestionId,
    placeholderValues.length,
  ]);

  // Initialize location when editing existing note
  useEffect(() => {
    if (props.note && selectedArea) {
      const areaName = getSelectedAreaName();
      setSelectedLocation(areaName);
    }
  }, [props.note, selectedArea]);

  // Handle venue details loading for edit mode
  useEffect(() => {
    // Only run this when we're not actively switching templates
    if (isSwitchingTemplate.current) {
      return;
    }

    if (
      props.note &&
      props.venueDetailsResponse.details.businessNoteHeadingSuggestions.length >
        0
    ) {
      // If we have venue details and a note to edit, ensure template is properly set
      if (
        props.note.business_note_heading_suggestions_id &&
        !selectedTemplate
      ) {
        // Only restore template if the current title is actually a template
        if (title && headlineRequiresTemplateParsing(title)) {
          const templateSuggestion =
            props.venueDetailsResponse.details.businessNoteHeadingSuggestions.find(
              (suggestion) =>
                suggestion.id.toString() ===
                props.note.business_note_heading_suggestions_id?.toString(),
            );

          if (templateSuggestion) {
            console.log(
              "Restoring template from venue details:",
              templateSuggestion.heading,
            );
            setSelectedTemplate(templateSuggestion.heading);
          }
        } else {
          console.log(
            "Preventing template restoration - current title is not a template:",
            title,
          );
        }
      }
    }
  }, [
    props.note,
    props.venueDetailsResponse.details.businessNoteHeadingSuggestions,
    selectedTemplate,
    title,
  ]);

  // Prevent template state from being restored when switching to non-template
  useEffect(() => {
    // Only run this when we're not actively switching templates
    if (isSwitchingTemplate.current) {
      return;
    }

    // Don't clear template state if we're editing an existing note with a template suggestion ID
    // This prevents clearing template state when editing existing template notes
    if (props.note && props.note.business_note_heading_suggestions_id) {
      return;
    }

    if (
      props.note &&
      title &&
      !headlineRequiresTemplateParsing(title) &&
      selectedTemplate
    ) {
      console.log(
        "Preventing template state restoration for non-template title",
      );
      setSelectedTemplate(null);
      setPlaceholderValues([]);
      // Don't clear selectedTemplateSuggestionId - it should persist for the selected suggestion
    }
  }, [props.note, title, selectedTemplate]);

  // Aggressive template state prevention - run this after all other useEffects
  useEffect(() => {
    // Only run this when we're not actively switching templates
    if (isSwitchingTemplate.current) {
      return;
    }

    // Don't clear template state if we're editing an existing note with a template suggestion ID
    // This prevents clearing template state when editing existing template notes
    if (props.note && props.note.business_note_heading_suggestions_id) {
      return;
    }

    if (props.note && title && !headlineRequiresTemplateParsing(title)) {
      // If we have a non-template title, ensure template state is cleared but keep suggestion ID
      if (selectedTemplate) {
        console.log(
          "Aggressively clearing selectedTemplate for non-template title",
        );
        setSelectedTemplate(null);
      }
      if (placeholderValues.length > 0) {
        console.log(
          "Aggressively clearing placeholderValues for non-template title",
        );
        setPlaceholderValues([]);
      }
      // Don't clear selectedTemplateSuggestionId - it should persist for the selected suggestion
      // This allows non-template suggestions to still have their ID posted
    }
  }, [
    props.note,
    title,
    selectedTemplate,
    placeholderValues.length,
    selectedTemplateSuggestionId,
  ]);

  // Add click outside handler for dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".searchable-input-container")) {
        setIsDropdownOpen(false);
        setSuggestionsVisible(false);
        setSearchTerm("");
        setSelectedIndex(-1);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleSearchableSuggestionSelect = useCallback(
    (suggestion: {
      id: string;
      label: string;
      business_notes_action_id?: number;
    }) => {
      if (suggestion.id === "manual") {
        // Handle manual add option
        setSelectedTemplate(null);
        setPlaceholderValues([]);
        setPreviewTitle(searchTerm);
        setStyledPreviewTitle(searchTerm);
        handleSuggestionSelect(suggestion.business_notes_action_id);
        setSelectedTemplateSuggestionId(null);
        setIsManualAdd(true); // Set manual mode when selecting manual add
      } else {
        // Handle regular suggestion - always set manual mode to false
        setIsManualAdd(false);

        if (headlineRequiresTemplateParsing(suggestion.label)) {
          // Template mode: set up template with editable placeholders
          setSelectedTemplate(suggestion.label);
          const parts = parseVenueNoteHeadlineTemplate(suggestion.label);
          const placeholdersCount = parts.filter(
            (part) => part.isPlaceholder && !part.isLocationPlaceholder,
          ).length;
          const initialPlaceholderValues = new Array(placeholdersCount).fill(
            "",
          );
          setPlaceholderValues(initialPlaceholderValues);

          const previewBase = suggestion.label.replace(
            /\[Space\]/g,
            selectedLocation || "[Space]",
          );

          setPreviewTitle(previewBase);
          const styledPreview = generateStyledPreview(
            suggestion.label,
            initialPlaceholderValues,
            selectedLocation || "",
          );
          setStyledPreviewTitle(styledPreview);

          setTitle(suggestion.label);
        } else {
          // Normal mode: set the full text (no placeholders)
          console.log("Setting non-template suggestion:", suggestion.label);

          // Set flag to prevent useEffect interference
          isSwitchingTemplate.current = true;

          // Clear template state first - use direct state setting
          console.log(
            "Clearing template state - selectedTemplate:",
            selectedTemplate,
          );
          setSelectedTemplate(null);
          setPlaceholderValues([]);
          // Don't clear selectedTemplateSuggestionId here - it will be set later for the selected suggestion

          // Then update title and preview
          setTitle(suggestion.label);
          setPreviewTitle(suggestion.label);
          setStyledPreviewTitle(suggestion.label);

          // Reset flag after state updates
          setTimeout(() => {
            isSwitchingTemplate.current = false;
            console.log("Template switching protection removed");

            // Only clear template-related state, NOT the suggestion ID
            console.log(
              "Clearing template state after protection removed (keeping suggestion ID)",
            );
            setSelectedTemplate(null);
            setPlaceholderValues([]);
            // Don't clear selectedTemplateSuggestionId here - it should persist for non-template suggestions
          }, 100); // Increased timeout to ensure state updates complete
        }
        handleSuggestionSelect(suggestion.business_notes_action_id);

        // Always set template suggestion ID for any selected suggestion
        console.log(
          "Setting selectedTemplateSuggestionId to:",
          suggestion.id,
          "for suggestion:",
          suggestion.label,
        );
        setSelectedTemplateSuggestionId(suggestion.id);
      }
      setIsDropdownOpen(false);
      setSuggestionsVisible(false);
      setSearchTerm("");
      setSelectedIndex(-1);
    },
    [searchTerm, selectedTemplate, selectedLocation, handleSuggestionSelect],
  );

  // Add keyboard navigation for dropdown
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isDropdownOpen) return;

      switch (event.key) {
        case "ArrowDown": {
          event.preventDefault();
          const filteredSearchResults = filteredSuggestions.filter(
            (suggestion) =>
              searchTerm
                ? suggestion.heading
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
                : true,
          );
          const suggestionsCount =
            filteredSearchResults.length + (searchTerm ? 1 : 0); // +1 for manual add option
          setSelectedIndex((prev) =>
            prev < suggestionsCount ? prev + 1 : prev,
          );
          break;
        }
        case "ArrowUp":
          event.preventDefault();
          setSelectedIndex((prev) => (prev > -1 ? prev - 1 : prev));
          break;
        case "Enter": {
          event.preventDefault();
          const filteredSearchResults = filteredSuggestions.filter(
            (suggestion) =>
              searchTerm
                ? suggestion.heading
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
                : true,
          );

          if (
            selectedIndex >= 0 &&
            selectedIndex < filteredSearchResults.length
          ) {
            const suggestion = filteredSearchResults[selectedIndex];
            handleSearchableSuggestionSelect({
              id: suggestion.id.toString(),
              label: suggestion.heading,
              business_notes_action_id: suggestion.business_notes_action_id,
            });
          } else if (
            selectedIndex === filteredSearchResults.length &&
            searchTerm
          ) {
            // Select manual add option
            setIsManualAdd(true);
            handleSuggestionSelect(0);
            setPlaceholderValues([]);
            setSelectedTemplate(null);
            setSuggestionsVisible(false);
            setIsDropdownOpen(false);
            setSearchTerm("");
            setSelectedIndex(-1);
          }
          break;
        }
        case "Escape":
          setIsDropdownOpen(false);
          setSuggestionsVisible(false);
          setSearchTerm("");
          setSelectedIndex(-1);
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    isDropdownOpen,
    selectedIndex,
    searchTerm,
    filteredSuggestions,
    handleSearchableSuggestionSelect,
    handleSuggestionSelect,
  ]);

  // SearchableInput event handlers
  const handleInputClick = () => {
    if (isSuggestionsEnabled) {
      setIsDropdownOpen(true);
      setSuggestionsVisible(true);
      setSearchTerm("");
      setSelectedIndex(-1);
    }
  };

  const handleCaretClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isSuggestionsEnabled) return;

    if (isDropdownOpen) {
      setIsDropdownOpen(false);
      setSuggestionsVisible(false);
      setSearchTerm("");
      setSelectedIndex(-1);
    } else {
      setIsDropdownOpen(true);
      setSuggestionsVisible(true);
      setSearchTerm("");
      setSelectedIndex(-1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isSuggestionsEnabled) return;

    const newValue = e.target.value;
    setTitle(newValue);

    // If we're editing an existing template note, update the template and preview
    if (props.note && props.note.business_note_heading_suggestions_id) {
      // Update the selected template to match the edited title
      setSelectedTemplate(newValue);

      // Update preview with current placeholder values
      if (placeholderValues.length > 0) {
        let previewText = newValue;

        // Replace [Space] placeholders with selected location
        previewText = previewText.replace(
          /\[Space\]/g,
          selectedLocation || "[Space]",
        );

        // Replace [XIcon] and [x] placeholders with actual values
        const xPlaceholders = parseVenueNoteHeadlineTemplate(newValue).filter(
          (part) => part.isPlaceholder && !part.isLocationPlaceholder,
        );

        // Replace each [XIcon] placeholder with its corresponding value
        xPlaceholders.forEach((placeholder, placeholderIndex) => {
          const placeholderValue =
            placeholderValues[placeholderIndex] || placeholder.text;
          previewText = previewText.replace(placeholder.text, placeholderValue);
        });

        setPreviewTitle(previewText);

        // Update styled preview with underlines
        const styledPreview = generateStyledPreview(
          newValue,
          placeholderValues,
          selectedLocation || "",
        );
        setStyledPreviewTitle(styledPreview);
      }
      return;
    }

    // If we're in template mode and user starts typing, clear template mode but preserve suggestion ID
    if (selectedTemplate) {
      setSelectedTemplate(null);
      setPlaceholderValues([]);
      // Don't clear selectedTemplateSuggestionId here - preserve it for the selected suggestion
      // This allows users to edit template suggestions while keeping the suggestion ID
    }

    // If user is typing manually (not selecting from suggestions), set manual mode
    if (newValue.length > 0) {
      const exactMatch = filteredSuggestions.find(
        (suggestion) => suggestion.heading === newValue,
      );
      if (exactMatch) {
        // If there's an exact match, this is not manual mode
        setIsManualAdd(false);
        setSelectedTemplateSuggestionId(exactMatch.id.toString());

        // CheckIcon if the exact match is a template
        if (headlineRequiresTemplateParsing(exactMatch.heading)) {
          setSelectedTemplate(exactMatch.heading);
          const parts = parseVenueNoteHeadlineTemplate(exactMatch.heading);
          const placeholdersCount = parts.filter(
            (part) => part.isPlaceholder && !part.isLocationPlaceholder,
          ).length;
          const initialPlaceholderValues = new Array(placeholdersCount).fill(
            "",
          );
          setPlaceholderValues(initialPlaceholderValues);
        } else {
          setSelectedTemplate(null);
          setPlaceholderValues([]);
        }
      } else {
        // If no exact match, this might be manual mode, but preserve suggestion ID if we had one
        setIsManualAdd(true);
        // Don't clear selectedTemplateSuggestionId here - preserve it for the selected suggestion
        setSelectedTemplate(null);
        setPlaceholderValues([]);
      }
    } else {
      // If input is cleared, reset manual mode
      setIsManualAdd(false);
      setSelectedTemplateSuggestionId(null);
      setSelectedTemplate(null);
      setPlaceholderValues([]);
    }

    // Update preview when typing
    setPreviewTitle(newValue);
    setStyledPreviewTitle(newValue);

    // Auto-search when typing
    setSearchTerm(newValue);
    setIsDropdownOpen(true);
    setSuggestionsVisible(true);
    setSelectedIndex(-1);
  };

  const handlePlaceholderChange = (index: number, value: string) => {
    const newPlaceholderValues = [...placeholderValues];
    newPlaceholderValues[index] = value;
    setPlaceholderValues(newPlaceholderValues);

    if (activeHeadlineTemplate) {
      let previewText = activeHeadlineTemplate;

      previewText = previewText.replace(
        /\[Space\]/g,
        selectedLocation || "[Space]",
      );

      const xPlaceholders =
        parseVenueNoteHeadlineTemplate(activeHeadlineTemplate).filter(
          (part) => part.isPlaceholder && !part.isLocationPlaceholder,
        );

      xPlaceholders.forEach((placeholder, placeholderIndex) => {
        const placeholderValue =
          newPlaceholderValues[placeholderIndex] || placeholder.text;
        previewText = previewText.replace(placeholder.text, placeholderValue);
      });

      setPreviewTitle(previewText);

      const styledPreview = generateStyledPreview(
        activeHeadlineTemplate,
        newPlaceholderValues,
        selectedLocation || "",
      );
      setStyledPreviewTitle(styledPreview);
    }
  };

  const handleAreaSelection = (value: string) => {
    console.log("?? handleAreaSelection called with value:", value);

    if (value === "0") {
      console.log("?? Setting selectedArea to null (no selection)");
      setSelectedArea(null);
      return;
    }

    // CheckIcon if the value contains a dot (parentId.subId format)
    if (value.includes(".")) {
      const [parentId, subId] = value.split(".");
      if (parentId && subId) {
        console.log(
          "?? Setting selectedArea with parentId:",
          parentId,
          "and subId:",
          subId,
        );
        setSelectedArea({
          parentId: Number(parentId),
          subId,
        });
      }
    } else {
      // Value is just a parentId (no subId)
      console.log("?? Setting selectedArea with parentId only:", value);
      setSelectedArea({
        parentId: Number(value),
        subId: "",
      });
    }
  };

  // Helper function to generate styled preview with underlines for replaced values
  const generateStyledPreview = (
    template: string,
    placeholderValues: string[],
    selectedLocation: string,
  ): string => {
    let styledPreview = template;

    // Replace [Space] placeholders with selected location (underlined)
    styledPreview = styledPreview.replace(
      /\[Space\]/g,
      `<u>${selectedLocation || "[Space]"}</u>`,
    );

    // Replace [XIcon] and [x] placeholders with actual values (underlined)
    let placeholderIndex = 0;
    styledPreview = styledPreview.replace(/\[(XIcon|[xX])\]/g, () => {
      const placeholderValue =
        placeholderValues[placeholderIndex] || "[XIcon]";
      placeholderIndex++;
      return `<u>${placeholderValue}</u>`;
    });

    return styledPreview;
  };

  // Helper function to filter suggestions based on area selection
  const getFilteredSuggestions = useCallback(
    (
      areaValue: string | null,
      allSuggestions: BusinessNoteHeadingSuggestion[],
    ): BusinessNoteHeadingSuggestion[] => {
      // If no area is selected, return all suggestions
      if (!areaValue) {
        return allSuggestions;
      }

      // Parse areaValue to determine filtering mode
      let parentId: string | null = null;

      if (areaValue === "0" || applyToWholeVenue) {
        // Whole Venue mode - look for'0' in area_ids_allowed
        parentId = "0";
      } else {
        // Specific area mode - extract parentId from areaValue
        const parts = areaValue.split(".");
        if (parts.length >= 1) {
          parentId = parts[0];
        }
      }

      if (!parentId) {
        return allSuggestions;
      }

      // Filter suggestions based on area_ids_allowed
      return allSuggestions.filter((suggestion) => {
        const areaIdsAllowed = suggestion.area_ids_allowed;

        // If area_ids_allowed is empty/null, treat as"no restriction" (show for all areas)
        if (!areaIdsAllowed || areaIdsAllowed.trim() === "") {
          return true;
        }

        // Parse CSV and check for exact token match
        const allowedIds = areaIdsAllowed.split(",").map((id) => id.trim());
        return allowedIds.includes(parentId);
      });
    },
    [applyToWholeVenue],
  );

  // Function to get the selected area name for SearchableInput
  const getSelectedAreaName = () => {
    if (applyToWholeVenue) {
      return "Whole venue";
    }

    if (!selectedArea) return "";

    const area =
      props.venueDetailsResponse.details.businessVenueNotesAreaLocations.find(
        (area) => area.id.toString() === selectedArea.parentId.toString(),
      );

    if (!area) return "";

    console.log("?? getSelectedAreaName - selectedArea:", selectedArea);
    console.log(
      "?? getSelectedAreaName - area:",
      area.name,
      "tags:",
      area.tags.map((t) => ({ id: t.id, name: t.name })),
    );

    // If we have a subId, find the specific tag
    if (selectedArea.subId && selectedArea.subId !== "") {
      const tag = area.tags.find(
        (tag) => tag.id.toString() === selectedArea.subId,
      );
      if (tag) {
        console.log("?? Returning tag name:", tag.name);
        return `${tag.name}`;
      }
    }

    // If no subId or tag not found, return the area name (parent)
    console.log("?? Returning area name (parent):", area.name);
    return area.name;
  };

  // Update filtered suggestions when area selection changes
  useEffect(() => {
    const areaValue = applyToWholeVenue
      ? "0"
      : selectedArea
        ? `${selectedArea.parentId}.${selectedArea.subId}`
        : null;
    const filtered = getFilteredSuggestions(
      areaValue,
      props.venueDetailsResponse.details.businessNoteHeadingSuggestions,
    );
    setFilteredSuggestions(filtered);

    // Only reset preview headline and placeholder text when area changes if we're not editing an existing note
    // This prevents clearing template state when editing existing template notes
    if (!props.note || !props.note.business_note_heading_suggestions_id) {
      setPreviewTitle("");
      setPlaceholderValues([]);
    }

    // CheckIcon if current selected suggestion is still valid after filtering
    if (selectedTemplateSuggestionId) {
      const isStillValid = filtered.some(
        (suggestion) =>
          suggestion.id.toString() === selectedTemplateSuggestionId,
      );
      if (!isStillValid) {
        // Clear selection if no longer valid
        setSelectedTemplateSuggestionId(null);
        setSelectedTemplate(null);
        setPlaceholderValues([]);
        setTitle("");
      }
    }
  }, [
    selectedArea,
    applyToWholeVenue,
    props.venueDetailsResponse.details.businessNoteHeadingSuggestions,
    getFilteredSuggestions,
    selectedTemplateSuggestionId,
    props.note,
  ]);

  // Update selectedLocation when area changes
  useEffect(() => {
    const areaName = getSelectedAreaName();
    setSelectedLocation(areaName);

    // Also update preview if we have a template
    if (activeHeadlineTemplate) {
      let previewText = activeHeadlineTemplate;

      previewText = previewText.replace(/\[Space\]/g, areaName || "[Space]");

      const xPlaceholders = parseVenueNoteHeadlineTemplate(
        activeHeadlineTemplate,
      ).filter(
        (part) => part.isPlaceholder && !part.isLocationPlaceholder,
      );

      xPlaceholders.forEach((placeholder, placeholderIndex) => {
        const placeholderValue =
          placeholderValues[placeholderIndex] || placeholder.text;
        previewText = previewText.replace(placeholder.text, placeholderValue);
      });

      setPreviewTitle(previewText);

      const styledPreview = generateStyledPreview(
        activeHeadlineTemplate,
        placeholderValues,
        areaName || "",
      );
      setStyledPreviewTitle(styledPreview);
    } else if (title && !activeHeadlineTemplate) {
      // If no template but we have a title, just update the preview with the title
      setPreviewTitle(title);
      setStyledPreviewTitle(title);
    }
  }, [
    selectedArea,
    applyToWholeVenue,
    activeHeadlineTemplate,
    placeholderValues,
    title,
  ]);

  return (
    <>
      <section style={modalStyle} className={cn(modalClassName)} role="none">
        {status === "pending" && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex justify-center items-start pt-[250px] z-50">
            <Loader />
          </div>
        )}

        <header
          className={`py-4 min-h-14 flex items-center justify-between text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn && !suggestionsVisible ? "sticky top-0 z-10" : "relative z-10"}`}
        >
          <StandardButton
            onClick={() => {
              fireEditFormClosed();
              props.onClose();
            }}
            variant="closeModal"
            aria-label="Close modal"
            tabIndex={0}
            icon={<XIcon color={isDark ? "#A39FE8" : "#352FC3"} />}
            className="flex-shrink-0"
          />
          <h1 className="title-s-semibold text-center text-fg-neutral-strong flex-1 px-4">
            {i18n.get("edit")}
          </h1>
          <div className="flex-shrink-0 w-10"></div>
          {" "}
          {/* Spacer to balance the layout */}
        </header>

        <div
          aria-label="Modal Content"
          className={`flex-1 min-h-0 ${suggestionsVisible ? "overflow-hidden" : "overflow-y-auto"}`}
          style={{ WebkitOverflowScrolling: "touch" }}
          role="none"
        >
          <div
            className={`self-stretch px-6 pt-2 flex-col justify-start items-center gap-2 flex ${suggestionsVisible ? "h-full mb-20" : ""}`}
          >
            <div className="self-stretch h-30 pt-2 opacity-80 flex-col justify-start items-start gap-1 flex">
              <div className="self-stretch h-12 flex-col justify-start items-start gap-2 flex">
                <div className="self-stretch !text-fg-brand-subtle label-s-medium">
                  {props.venueDetailsResponse.details.name}
                </div>
                <div className="self-stretch text-fg-neutral-strong title-s-semibold">
                  {i18n.get("publicNote")}
                </div>
              </div>
              <div className="self-stretch text-fg-neutral-strong body-l-semibold md:title-s-semibold pt-1">
                {i18n.get("shareYourInsightsToHelp")}
              </div>
            </div>

            <div className="self-stretch rounded-lg outline outline-1 outline-offset-[-1px] outline-[#deddf7] inline-flex flex-col justify-center items-start">
              {/* Apply whole venue */}
              <div className="self-stretch flex flex-col gap-2 p-4">
                <h2 className="title-s-semibold text-fg-neutral-strong">
                  {i18n.get("doesTheNoteApplyToTheWholeVenue")}
                </h2>
                <div className="flex gap-4">
                  <RadioButton
                    checked={applyToWholeVenue}
                    onCheck={(value) => {
                      if (!applyToWholeVenue && value) {
                        setApplyToWholeVenue(true);
                        setSelectedArea(null);
                      }
                    }}
                    label="Yes"
                    name="applyToWholeVenue"
                    value="yes"
                  />
                  <RadioButton
                    checked={!applyToWholeVenue}
                    onCheck={(value) => {
                      if (applyToWholeVenue && value) {
                        setApplyToWholeVenue(false);
                      }
                    }}
                    label="No"
                    name="applyToWholeVenue"
                    value="no"
                  />
                </div>
              </div>

              {/* Area Selection - Only shown when not applying to whole venue */}
              {!applyToWholeVenue && (
                <div className="self-stretch flex flex-col pl-4 pr-4">
                  {(() => {
                    const dropdownValue = selectedArea
                      ? selectedArea.subId && selectedArea.subId !== ""
                        ? `${selectedArea.parentId}.${selectedArea.subId}`
                        : `${selectedArea.parentId}.0`
                      : "0";

                    const optionsList = [
                      { id: "0", label: i18n.get("selectArea") },
                      ...props.venueDetailsResponse.details.businessVenueNotesAreaLocations
                        .filter((area) => area.id !== "Business")
                        .map((area) => ({
                          id: area.id.toString(),
                          label: area.name,
                          subTitles: area.tags.map((tag) => ({
                            id: tag.id.toString(),
                            label: tag.name,
                          })),
                        })),
                    ];

                    console.log("?? SelectDropDown value calculation:", {
                      selectedArea,
                      dropdownValue,
                      hasSubId:
                        selectedArea?.subId && selectedArea.subId !== "",
                      parentId: selectedArea?.parentId,
                      subId: selectedArea?.subId,
                    });

                    console.log(
                      "?? SelectDropDown options:",
                      optionsList.map((opt) => ({
                        id: opt.id,
                        label: opt.label,
                      })),
                    );
                    return (
                      <SelectDropDown
                        label={i18n.get("noteLocation")}
                        optionsList={optionsList}
                        value={dropdownValue}
                        required={true}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleAreaSelection(value);
                        }}
                        optgroup={false}
                      />
                    );
                  })()}
                </div>
              )}

              {/* Combined Searchable Note Headline Input */}
              <div className="w-full flex-col justify-center items-start gap-1 flex p-4">
                <div
                  className={cn(
                    "relative w-full searchable-input-container",
                    !isSuggestionsEnabled && "opacity-50 pointer-events-none",
                  )}
                >
                  <div className="w-full relative">
                    <FloatingLabelInput
                      type="text"
                      label={i18n.get("title")}
                      value={title}
                      required={true}
                      placeholder={
                        isSuggestionsEnabled
                          ? i18n.get("noteHeadlineTemplate")
                          : "Please select an area first"
                      }
                      onChange={handleInputChange}
                      onFocus={handleInputClick}
                      onEnter={() => {
                        if (!isDropdownOpen && isSuggestionsEnabled) {
                          setIsDropdownOpen(true);
                          setSuggestionsVisible(true);
                          setSearchTerm("");
                          setSelectedIndex(-1);
                        }
                      }}
                    />
                    <ChevronDown
                      className={cn(
                        "absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 transition-transform duration-200",
                        isSuggestionsEnabled
                          ? "text-fg-neutral-subtle cursor-pointer hover:text-fg-brand-subtle"
                          : "text-fg-neutral-minimal cursor-not-allowed",
                        isDropdownOpen && "rotate-180",
                      )}
                      onClick={
                        isSuggestionsEnabled ? handleCaretClick : undefined
                      }
                    />
                  </div>

                  {/* Placeholder inputs for template mode */}
                  {activeHeadlineTemplate && (
                    <div className="mt-3 space-y-2">
                      {parseVenueNoteHeadlineTemplate(activeHeadlineTemplate)
                        .filter(
                          (part) =>
                            part.isPlaceholder && !part.isLocationPlaceholder,
                        )
                        .map((part, index) => (
                          <FloatingLabelInput
                            key={`placeholder-row-${index}`}
                            type="text"
                            label={`Placeholder ${part.text}`}
                            value={placeholderValues[index] || ""}
                            onChange={(e) =>
                              handlePlaceholderChange(index, e.target.value)
                            }
                            onFocus={(e) => e.stopPropagation()}
                            onClear={() => handlePlaceholderChange(index, "")}
                            placeholder={i18n.get("addTheMissingDetails")}
                          />
                        ))}
                    </div>
                  )}

                  {/* Suggestions Dropdown */}
                  {isDropdownOpen && isSuggestionsEnabled && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-bg-brand-secondary-default border-2 border-primary-500 rounded-[8px] shadow-lg z-50 max-h-80 overflow-hidden">
                      <div className="max-h-60 overflow-y-auto">
                        {filteredSuggestions.length === 0 ? (
                          <div className="px-3 py-2 label-m-medium text-fg-neutral-subtle">
                            No suggestions for this area
                          </div>
                        ) : (
                          filteredSuggestions
                            .filter((suggestion) =>
                              searchTerm
                                ? suggestion.heading
                                    .toLowerCase()
                                    .includes(searchTerm.toLowerCase())
                                : true,
                            )
                            .map((suggestion, index) => (
                              <div
                                key={suggestion.id}
                                className={cn(
                                  "px-3 py-2 cursor-pointer label-m-medium transition-colors duration-150",
                                  index === selectedIndex
                                    ? "bg-container-brand-minimal text-fg-neutral-strong"
                                    : "hover:bg-container-neutral-minimal text-fg-neutral-strong",
                                )}
                                onClick={() =>
                                  handleSearchableSuggestionSelect({
                                    id: suggestion.id.toString(),
                                    label: suggestion.heading,
                                    business_notes_action_id:
                                      suggestion.business_notes_action_id,
                                  })
                                }
                              >
                                <div className="flex items-center gap-2">
                                  {suggestion.business_notes_action_id ===
                                    9 && (
                                    <div title="Time sensitive">
                                      <img
                                        src="/images/clock-countdown.svg"
                                        alt="Time sensitive"
                                        className="h-4 w-4 text-fg-error-subtle"
                                      />
                                    </div>
                                  )}
                                  {suggestion.business_notes_action_id ===
                                    10 && (
                                    <div title="Non-Time sensitive">
                                      <InfoIcon className="h-4 w-4 text-fg-neutral-strong" />
                                    </div>
                                  )}
                                  <span>{suggestion.heading}</span>
                                </div>
                              </div>
                            ))
                        )}

                        {/* Manual add option */}
                        {searchTerm && filteredSuggestions.length > 0 && (
                          <div className="border-t border-border-token-neutral-minimal">
                            <div
                              className={cn(
                                "px-3 py-2 cursor-pointer label-m-medium transition-colors duration-150 flex items-center gap-2",
                                selectedIndex ===
                                  filteredSuggestions.filter((suggestion) =>
                                    searchTerm
                                      ? suggestion.heading
                                          .toLowerCase()
                                          .includes(searchTerm.toLowerCase())
                                      : true,
                                  ).length
                                  ? "bg-container-brand-minimal text-fg-neutral-strong"
                                  : "hover:bg-container-neutral-minimal text-fg-brand-strong",
                              )}
                              onClick={() => {
                                setIsManualAdd(true);
                                handleSuggestionSelect(0);
                                setPlaceholderValues([]);
                                setSelectedTemplate(null);
                                setSuggestionsVisible(false);
                                setIsDropdownOpen(false);
                                setSearchTerm("");
                                setSelectedIndex(-1);
                              }}
                            >
                              <Plus className="h-4 w-4" />
                              Add"{searchTerm}" manually
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div
                  id="charCountTitle"
                  className="label-s-medium text-fg-neutral-subtle mt-3 mb-1"
                >
                  {!isSuggestionsEnabled ? (
                    <span className="text-fg-neutral-subtle">
                      Please select"Yes" for whole venue or choose a specific
                      area to enable suggestions
                    </span>
                  ) : (
                    <>
                      <span className={title.length < 5 ? "text-red-600" : ""}>
                        {title.length}
                      </span>
                      /50 Characters - at least 5 characters required
                    </>
                  )}
                </div>
              </div>

              <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-[#deddf7]"></div>

              <div className="self-stretch inline-flex flex-col justify-center items-start p-4">
                {!isManualAdd && (
                  <div className="self-stretch justify-center label-s-medium leading-none">
                    {previewTitle
                      ? i18n.get("previewNoteHeadline")
                      : i18n.get("preview")}
                  </div>
                )}

                {isManualAdd && (
                  <div className="self-stretch inline-flex justify-start items-center gap-2.5">
                    <div className="justify-center label-s-medium leading-none">
                      {i18n.get("preview")}
                    </div>

                    {previewTitle && (
                      <div
                        data-property-1="Blue badge with leading icon"
                        className="pl-3 pr-4 py-1 bg-white rounded-[20px] outline outline-1 outline-offset-[-1px] outline-[#352fc3] flex justify-center items-center gap-2 overflow-hidden"
                      >
                        <div title="Preview">
                          <InfoIcon className="h-4 w-4 text-fg-neutral-strong" />
                        </div>
                        <div className="justify-center label-s-medium leading-none">
                          New note headline
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="inline-flex justify-start items-center gap-2.5">
                  <div className="justify-center">
                    <span
                      className="body-l-semibold leading-none"
                      dangerouslySetInnerHTML={{
                        __html: styledPreviewTitle
                          ? styledPreviewTitle
                          : previewTitle
                            ? previewTitle
                            : "--",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-[#deddf7] mt-2"></div>

            {/* Note Validity Date Selection - Always Visible */}
            <div className="self-stretch flex flex-col gap-2 p-4">
              <div className="my-2 self-stretch">
                <div className="flex flex-col gap-4">
                  <Popover>
                    <PopoverTrigger asChild>
                      <StandardButton
                        variant="outline"
                        fullWidth={true}
                        className={`justify-between text-left font-normal ${
                          !dateRange?.from &&
                          !dateRange?.to &&
                          "text-muted-foreground"
                        }`}
                      >
                        <span>
                          {dateRange?.from && dateRange?.to ? (
                            <>
                              {format(dateRange.from, "LLL dd, y")} -{" "}
                              {format(dateRange.to, "LLL dd, y")}
                            </>
                          ) : dateRange?.from ? (
                            <>
                              {format(dateRange.from, "LLL dd, y")} - No end
                              date set
                            </>
                          ) : (
                            <span>{i18n.get("noteValidity")}</span>
                          )}
                        </span>
                        <CalendarIcon className="h-4 w-4" />
                      </StandardButton>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-4 z-[9999] bg-white text-black rounded-md shadow-lg border"
                      align="start"
                      sideOffset={5}
                    >
                      <Calendar
                        mode="range"
                        required={true}
                        selected={dateRange}
                        onSelect={(selected) => {
                          if (
                            !dateRange?.from ||
                            (dateRange.from && dateRange.to)
                          ) {
                            // If no date selected or both dates are selected, start new selection
                            setDateRange({
                              from: selected?.from,
                              to: undefined,
                            });
                          } else {
                            // If we have a from date but no to date, complete the range
                            if (selected?.from && selected?.to) {
                              setDateRange(selected);
                            }
                          }
                        }}
                        initialFocus
                        disabled={(date) =>
                          date < new Date(new Date().setHours(0, 0, 0, 0))
                        }
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 flex mt-2 mb-4">
              <div className=" w-full" tabIndex={0}>
                <Textarea
                  id="review"
                  placeholder={i18n.get("noteDetails")}
                  className="scrollbar-thin h-28"
                  value={notes}
                  aria-describedby="charCount"
                  onChange={(e) => {
                    setNotes(e.target.value);
                  }}
                  maxLength={150}
                  ref={noteRef}
                  required={false}
                />
                <div
                  id="charCount"
                  className="label-s-medium text-fg-neutral-subtle mt-3"
                >
                  <span
                    className={
                      isNoteTextMandatory && notes.length < 5
                        ? "text-red-600"
                        : ""
                    }
                  >
                    {notes.length}
                  </span>
                  /150 Characters
                  {isNoteTextMandatory
                    ? " - at least 5 characters required"
                    : ""}
                </div>
              </div>
            </div>
          </div>
        </div>

        <footer
          className={`py-3 min-h-14 flex items-center justify-center text-center px-6 border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn && !suggestionsVisible ? "sticky bottom-0 z-10" : ""}`}
        >
          <div className="flex-col justify-end items-center gap-4 flex md:w-full">
            <div className="w-full justify-center items-center inline-flex">
              <WideButton
                disabled={isSubmitDisabled()} // Disable if conditions are not met
                className="grow"
                onClick={() => {
                  submit();
                }}
                aria-label="Update venue note"
              >
                {i18n.get("update")}
              </WideButton>
            </div>
          </div>
        </footer>
      </section>
    </>
  );
}

export default EditVenueNote;
